import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { money } from "@/lib/format";
import { cn } from "@/lib/cn";
import { useFunder } from "@/funder/FunderContext";

function Stat({ label, value, tone, testid, format = "money" }) {
  const display =
    format === "hours"
      ? `${Number(value || 0).toLocaleString("en-US", { maximumFractionDigits: 1 })} h`
      : money(value);
  return (
    <div className="shrink-0 px-4 py-2" data-testid={testid}>
      <p className="whitespace-nowrap text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p
        className={cn(
          "nums whitespace-nowrap text-sm font-semibold",
          tone === "acsa" ? "text-status-paid" : tone === "client" ? "text-primary" : "text-foreground"
        )}
      >
        {display}
      </p>
    </div>
  );
}

/** Read-only horizontal billing strip pinned across the bottom of the workbench. */
export function BillingStrip({ matterId, version, isAcsa, rate }) {
  const { abbr } = useFunder();
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    Promise.all([
      api.get(`/matters/${matterId}/billing/full`),
      api.get(`/matters/${matterId}/ledger?payer=Client`),
      api.get(`/matters/${matterId}/ledger?payer=ACSA`),
      api.get(`/matters/${matterId}/payments`),
    ])
      .then(([b, lc, la, pays]) => {
        if (!active) return;
        setData({ billing: b.data, lc: lc.data, la: la.data, payments: pays.data });
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [matterId, version]);

  if (!data) {
    return (
      <div className="grid place-items-center py-4" data-testid="billing-strip-loading">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  const c = data.billing.client;
  const a = data.billing.acsa;
  const l1 = a.rounds.reduce((s, r) => s + r.l1, 0);
  const l2 = a.rounds.reduce((s, r) => s + r.l2, 0);
  const l3 = a.rounds.reduce((s, r) => s + r.l3, 0);
  const creditApplied = (data.payments || [])
    .filter((p) => !p.void && p.method === "Retainer")
    .reduce((s, p) => s + (p.amount || 0), 0);
  const billed = (data.lc.billed_to_date || 0) + (data.la.billed_to_date || 0);
  const paid = (data.lc.payments_received || 0) + (data.la.payments_received || 0);
  const outstanding = (data.lc.amount_due || 0) + (data.la.amount_due || 0);
  const totalHours = rate ? c.charges_to_date / rate : c.hours_shown || 0;

  return (
    <div
      className="flex divide-x divide-border overflow-x-auto rounded-lg border border-border bg-surface shadow-card"
      data-testid="billing-strip"
    >
      <Stat label="Approved charges" value={c.charges_to_date} testid="strip-charges" />
      <Stat label="Total hours" value={totalHours} format="hours" testid="strip-total-hours" />
      {isAcsa && <Stat label="L1 credit" value={l1} testid="strip-l1" />}
      {isAcsa && <Stat label="L2 credit" value={l2} testid="strip-l2" />}
      {isAcsa && <Stat label="L3 credit" value={l3} testid="strip-l3" />}
      {isAcsa && <Stat label={`${abbr} coverage`} value={a.acsa_coverage} tone="acsa" testid="strip-acsa-coverage" />}
      <Stat label="Credit applied" value={creditApplied} testid="strip-credit-applied" />
      <Stat label="Client resp." value={c.responsibility_to_date} tone="client" testid="strip-client-resp" />
      {isAcsa && <Stat label={`${abbr} resp.`} value={a.responsibility_to_date} tone="acsa" testid="strip-acsa-resp" />}
      <Stat label="Billed" value={billed} testid="strip-billed" />
      <Stat label="Paid" value={paid} testid="strip-paid" />
      <Stat label="Outstanding" value={outstanding} testid="strip-outstanding" />
    </div>
  );
}
